import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import CustomLoading from '../../../util_components/custom_loading';
import { get_api as getApi } from '../../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../../utils/urls';
import { image_error_replace } from '../../../../util_functions';
import { format_actor, format_date, UNKNOWN } from './format';
import '../teachers_list/teachers_list.css';

// Hidden Teachers: who is currently hidden from trial students, and why. PROD-9278 recorded the
// reason and the actor but shipped no display, so until now the audit was queryable and invisible.
// Read-only by decision — unhiding stays on the teacher profile, where the note modal lives.
//
// Built from the same elements as the teachers list next to it in the nav, so the two read as one
// section rather than two designs.

export default function HiddenTeachers() {
  const [rows, set_rows] = useState([]);
  const [loading, set_loading] = useState(true);
  const [error, set_error] = useState('');
  const iana_timezone = useSelector((state) => state.home.iana_timezone);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const url = getApiUrl('/v2/cms_user/teachers/hidden_from_trials', null, true);
        const response = await getApi(url, true);
        if (cancelled) return;
        set_rows(response?.data?.data || []);
        set_error('');
      } catch (e) {
        if (cancelled) return;
        set_error(e?.response?.data?.reason || 'Could not load hidden teachers.');
      } finally {
        if (!cancelled) set_loading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <CustomLoading />;

  return (
    <div className="common-page-container">
      <Helmet>
        <title>Hidden Teachers</title>
      </Helmet>
      <div className="common-page-title">
        <span style={{ color: 'var(--text-muted)' }}>
          Hidden Teachers{' '}
          {!error && (
            <span style={{ color: 'var(--text-primary)', fontSize: '18px' }}>{rows.length}</span>
          )}
        </span>
      </div>
      {error ? <div style={{ color: 'var(--state-error-text)' }}>{error}</div> : null}
      <table style={{ width: '100%' }}>
        <tbody>
          <tr className="teachers-details-row">
            <td>
              <img
                src="https://images.myyogateacher.com/teacher_web_app/Placeholder_Profile_Pic@3x.png"
                alt="pic"
              />
            </td>
            <td>TEACHER</td>
            <td>STATUS</td>
            <td>POINTS</td>
            <td>HIDDEN SINCE</td>
            <td>HIDDEN BY</td>
            <td>REASON</td>
            <td>OPEN</td>
          </tr>
          {/* Only when the fetch SUCCEEDED and came back empty. On a failure this line would tell
              an operator nobody is hidden, which is the opposite of what we know. */}
          {!error && rows.length === 0 ? (
            <tr>
              <td className="low-text" colSpan={8}>
                No teachers are hidden from trials.
              </td>
            </tr>
          ) : null}
          {error ? (
            <tr>
              <td className="low-text" colSpan={8}>
                Could not load the list — this is not a statement that nobody is hidden.
              </td>
            </tr>
          ) : null}
          {rows.map((row) => (
            <tr key={row.teacher_uuid}>
              <td>
                <img src={row.profile_photo} alt="pic" onError={image_error_replace} />
              </td>
              <td className="list-name-email">
                <div>{row.teacher_name}</div>
                <div className="list-email">{row.email}</div>
              </td>
              <td className="low-text">{row.status}</td>
              <td className="low-text">{row.balance}</td>
              <td className="low-text">{format_date(row.hidden_since, iana_timezone)}</td>
              <td className="low-text">{format_actor(row)}</td>
              <td className="low-text" style={{ maxWidth: '260px' }}>
                {row.reason || UNKNOWN}
              </td>
              <td>
                <a
                  className="ct-small-btn link-btn"
                  href={`/teacherprofile?id=${encodeURIComponent(row.teacher_uuid)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Profile
                </a>
                <a
                  className="ct-small-btn link-btn"
                  style={{ marginLeft: '6px' }}
                  href={`/trial_points/${encodeURIComponent(row.teacher_uuid)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Points
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
